import { COLORS, MovedState, RadarEntry, RadarRings } from './types'

/*
Updated: 2026-07-15
Sources:
  - https://sennder.atlassian.net/wiki/spaces/AE/pages/5024907276/AI+Tech+Evaluation+and+Backlog
  - https://sennder.atlassian.net/wiki/spaces/AE/pages/4643323936/Tools+and+Resources
  - https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder
  - https://gitlab.com/sennder/engineering-effectiveness/platform/genai/ai-assistant-config
  - https://gitlab.com/sennder/engineering-effectiveness/platform/genai/config-merger
 */

export const AI_RINGS: RadarRings = {
  ADOPT: {
    id: 'adopt',
    name: 'ADOPT',
    color: COLORS.GREEN
  },
  TRIAL: {
    id: 'trial',
    name: 'TRIAL',
    color: COLORS.BLUE
  },
  HOLD: {
    id: 'hold',
    name: 'HOLD',
    color: COLORS.RED
  }
}

export interface AIRadarQuadrant {
  id: string
  name: string
  entries: Array<RadarEntry>
}

// ========== SENNDER-BUILT AI TOOLS AND INTEGRATIONS ==========

const sennderEntries: Array<RadarEntry> = [
  // Adopt
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Sennder-built AI engineering assistant now in adopt.'
      }
    ],
    url: 'https://jarvis.sennder.tech',
    key: 'jarvis',
    id: 'jarvis',
    title: 'Jarvis',
    quadrant: '',
    description:
      'A sennder-built AI engineering assistant with integrated access to GitLab, Confluence, and Datadog through MCP servers. Designed for engineering tasks like incident investigation, debugging sessions, and answering engineering questions with full sennder context.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'AI-powered MR reviewing agent in production.'
      }
    ],
    url: 'https://gitlab.com/sennder/engineering-effectiveness/platform/genai/merge-request-reviewer',
    key: 'mr-reviewer',
    id: 'mr-reviewer',
    title: 'MR Reviewer (sennReview)',
    quadrant: '',
    description:
      'An AI-powered merge request reviewer built at sennder. Comment "sennreview" on any GitLab MR (with optional custom instructions) and receive automated code review feedback within about a minute. Provides an extra pair of AI eyes to catch issues before human review.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Central repository for Copilot customizations.'
      },
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-07-15'),
        description:
          'Renamed to AI Assistant Config as sennder moved from Copilot to Claude.'
      }
    ],
    url: 'https://gitlab.com/sennder/engineering-effectiveness/platform/genai/ai-assistant-config',
    key: 'ai-assistant-config',
    id: 'ai-assistant-config',
    title: 'AI Assistant Config',
    quadrant: '',
    description:
      'A curated collection of AI assistant customizations including instructions, skills, hooks, and MCP server configurations maintained by the engineering effectiveness team. Clone the repo and use Config Merger to apply these settings to your IDE for an enhanced AI development experience.'
  },
  // Trial
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Merge multiple config directories with symlinks.'
      }
    ],
    url: 'https://gitlab.com/sennder/engineering-effectiveness/platform/genai/config-merger',
    key: 'config-merger',
    id: 'config-merger',
    title: 'Config Merger',
    quadrant: '',
    description:
      'A CLI tool that merges multiple configuration directories into one using symlinks. Use it to combine your personal AI assistant settings with the shared ai-assistant-config repo without conflicts. Supports glob patterns, conflict resolution with priority lists, and dry-run mode for safe previews.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Fast local Confluence export for AI context.'
      }
    ],
    url: 'https://gitlab.com/sennder/business-platform/tools/confluence2md',
    key: 'confluence2md',
    id: 'confluence2md',
    title: 'Confluence2MD',
    quadrant: '',
    description:
      'Download entire Confluence page trees as clean Markdown files for fast AI context. No installation needed (run with uvx). Add a confluence2md.yaml to your project, run the tool, and your AI assistant can reference all your team docs locally. Skips unchanged pages on re-runs.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-06-08'),
        description: 'AI assistant skill for sennder DSF components.'
      }
    ],
    url: 'https://gitlab.com/sennder/engineering-effectiveness/platform/genai/ai-assistant-config/-/blob/main/skills/sennder-dsf/SKILL.md',
    key: 'sennder-dsf-skill',
    id: 'sennder-dsf-skill',
    title: 'DSF AI Assistant Skill',
    quadrant: '',
    description:
      "An AI assistant skill that helps Claude and other AI assistants use sennder's Design System Foundation (DSF) correctly. When activated, it points the AI to the authoritative documentation inside @sennder/design-system-core — covering imports, theming, component conventions, events, Vue integration, forms, and E2E testing. Add it via the ai-assistant-config repo."
  }
  // Hold
]

// ========== TOOLS ==========

const toolsEntries: Array<RadarEntry> = [
  // Adopt
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-07-14'),
        description: 'Recommended MCP for Snowflake access.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/SD/pages/5190254607/Snowflake+MCPs',
    key: 'snowflake-mcp',
    id: 'snowflake-mcp',
    title: 'Snowflake MCP',
    quadrant: '',
    description:
      'Query Snowflake data warehouse from your AI assistant for data analysis and exploration. To check for which tools the Snowflake MCP is available and how to configure it, please see under LEARN MORE.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for monitoring and debugging.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Datadog',
    key: 'datadog-mcp',
    id: 'datadog-mcp',
    title: 'Datadog MCP',
    quadrant: '',
    description:
      'Give your AI assistant access to Datadog for investigating alerts, analyzing logs, and debugging production issues. Configure with: {"type": "http", "url": "https://mcp.datadoghq.eu/api/unstable/mcp-server/mcp"}. Useful during incident investigations.'
  },
  {
    timeline: [
      {
        moved: MovedState.Up,
        ringId: 'adopt',
        date: new Date('2026-07-15'),
        description:
          'New AI pricing models make token optimization more important than ever.'
      },
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: '73% token savings in typical usage.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/5196775432/Tech+Radar+RTK+Rust+Token+Killer',
    key: 'rtk',
    id: 'rtk',
    title: 'RTK (Rust Token Killer)',
    quadrant: '',
    description:
      'Reduce AI token usage and context window size by optimizing shell commands. Reports show significant token savings in typical workflows, making expensive model usage more sustainable for daily development tasks.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'One of the most used MCPs on the team.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#GitLab',
    key: 'gitlab-mcp',
    id: 'gitlab-mcp',
    title: 'GitLab MCP',
    quadrant: '',
    description:
      'Give your AI assistant direct access to GitLab repositories, merge requests, and CI pipelines. Uses OAuth (inherits your permissions) and is one of the most-used MCPs at sennder. Configure via: {"type": "http", "url": "https://gitlab.com/api/v4/mcp"}'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for up-to-date library docs.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Context-7',
    key: 'context7-mcp',
    id: 'context7-mcp',
    title: 'Context7 MCP',
    quadrant: '',
    description:
      'Get the latest documentation and code examples for any library directly in your AI assistant. Useful when working with rapidly-evolving frameworks where training data may be outdated. Provides accurate, up-to-date API references on demand.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for code navigation.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Serena',
    key: 'serena-mcp',
    id: 'serena-mcp',
    title: 'Serena MCP',
    quadrant: '',
    description:
      'An MCP that gives your AI assistant deep code navigation capabilities — reading, searching, and understanding your codebase structure. Helpful for large monorepos where the AI needs precise symbol lookup and cross-file references. Run with uvx, no installation needed.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for Confluence access.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Atlassian-(Confluence)',
    key: 'atlassian-mcp',
    id: 'atlassian-mcp',
    title: 'Atlassian (Confluence) MCP',
    quadrant: '',
    description:
      'Connect your AI assistant to Confluence for searching and reading documentation. Uses OAuth authentication (inherits your permissions). Important: only select Confluence in allowed applications to avoid permission errors. Slower than Confluence2MD for bulk operations.'
  },
  // Trial
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Available in internal Jarvis agent.'
      }
    ],
    url: '#',
    key: 'backstage-mcp',
    id: 'backstage-mcp',
    title: 'Backstage MCP',
    quadrant: '',
    description:
      'Access the sennder Developer Portal catalog, service ownership, and documentation directly from your AI assistant. Available through the internal Jarvis agent for engineering context lookups.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Used for reviewing datasources and boilerplate.'
      }
    ],
    url: '#',
    key: 'datahub-mcp',
    id: 'datahub-mcp',
    title: 'DataHub MCP',
    quadrant: '',
    description:
      'Connect your AI assistant to DataHub for discovering data sources, reviewing schemas, and generating boilerplate code for new data experiments. Useful for data engineers starting new analytics pipelines.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for design implementation.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Figma',
    key: 'figma-mcp',
    id: 'figma-mcp',
    title: 'Figma MCP',
    quadrant: '',
    description:
      'Connect your AI assistant to Figma designs for implementing UI from mockups. Best for rapid prototyping and high-level layouts. Note: MCP may not automatically map DSF component relationships, so use it for quick iterations rather than pixel-perfect implementations.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for diagramming.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#Miro',
    key: 'miro-mcp',
    id: 'miro-mcp',
    title: 'Miro MCP',
    quadrant: '',
    description:
      'Let your AI assistant create and update Miro boards for system design and architecture documentation. Impressive results for visualizing system flows, database schemas, and business processes. Configure with: {"url": "https://mcp.miro.com", "type": "http"}'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Recommended MCP for local database access.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/4736909317/MCP+servers+for+sennder#DBHub',
    key: 'dbhub-mcp',
    id: 'dbhub-mcp',
    title: 'DBHub MCP',
    quadrant: '',
    description:
      'Connect your AI assistant to local databases for querying, schema exploration, and generating SQL. Supports PostgreSQL and other databases. Important: only use with local databases — avoid connecting to AWS databases for security reasons.'
  }
  // Hold
]

// ========== PLATFORMS ==========

const platformsEntries: Array<RadarEntry> = [
  // Adopt
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Official sennder AI chat platform.'
      }
    ],
    url: 'https://chat.sennder.ai/',
    key: 'librechat',
    id: 'librechat',
    title: 'LibreChat',
    quadrant: '',
    description:
      'The official sennder AI chat platform at chat.sennder.ai. A general-purpose assistant that supports defining agents, prompts, memory, file attachments, and MCP connections. Use it for asking questions, analyzing data, generating text, and documentation tasks.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Unified LLM API gateway now in adopt.'
      }
    ],
    url: 'https://llms.sennder.ai/ui/',
    key: 'litellm',
    id: 'litellm',
    title: 'LiteLLM',
    quadrant: '',
    description:
      'A unified gateway to call 100+ LLM APIs (OpenAI, Anthropic, Gemini, etc.) using a single consistent format. Provides load balancing, cost tracking, and rate limiting across multiple LLM deployments. Use it when building internal AI features that need multiple model providers.'
  },
  {
    timeline: [
      {
        moved: MovedState.Up,
        ringId: 'adopt',
        date: new Date('2026-07-15'),
        description:
          'Now the official sennder coding assistant, replacing GitHub Copilot.'
      },
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Anthropic terminal-based AI coding agent.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/5149065234/Decision+AI+Coding+Tooling+Copilot+Claude+Code+July+2026',
    key: 'claude-code',
    id: 'claude-code',
    title: 'Claude Code',
    quadrant: '',
    description:
      'Anthropic terminal-based AI coding agent that can read, write, and execute code in your project. Now the official sennder coding assistant, replacing GitHub Copilot. Powerful for complex multi-file refactoring and agentic workflows.'
  },
  // Trial
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'AI-powered data analytics platform.'
      }
    ],
    url: 'https://www.dataleap.ai/',
    key: 'dataleap',
    id: 'dataleap',
    title: 'DataLeap',
    quadrant: '',
    description:
      'An AI-powered analytics platform that helps teams explore data, generate insights, and build dashboards using natural language. Being trialed for accelerating data-driven decision making and reducing the barrier to data exploration for engineering teams.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Visual workflow automation platform.'
      }
    ],
    url: 'https://n8n.cloud.sennder.com/',
    key: 'n8n',
    id: 'n8n',
    title: 'N8N',
    quadrant: '',
    description:
      'An open-source workflow automation platform with a visual node-based interface. Connect and automate tasks across 400+ services with no-code or custom JavaScript/Python logic. Self-hosted at sennder for full data privacy. Use it to build AI-powered automation pipelines.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Open-source terminal AI assistant.'
      }
    ],
    url: 'https://opencode.ai/',
    key: 'opencode',
    id: 'opencode',
    title: 'OpenCode',
    quadrant: '',
    description:
      'An open-source AI coding assistant that runs directly in the terminal. Supports multiple AI providers (OpenAI, Anthropic) and can read, write, and edit files within your project. A lightweight alternative to GUI-based AI tools for developers who prefer staying in the command line.'
  },
  // Hold
  {
    timeline: [
      {
        moved: MovedState.Down,
        ringId: 'hold',
        date: new Date('2026-07-15'),
        description: 'Replaced by Claude Code as official coding assistant.'
      },
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Official sennder coding assistant.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/5149065234/Decision+AI+Coding+Tooling+Copilot+Claude+Code+July+2026',
    key: 'github-copilot',
    id: 'github-copilot',
    title: 'GitHub Copilot',
    quadrant: '',
    description:
      'The former official sennder AI coding assistant, now replaced by Claude Code. Supported standard models (GPT-5 mini, no quota) and premium models (Claude 4.6, quota-based) with Agent, Edit, Ask, and Plan modes plus CLI and IDE plugins for VS Code and PyCharm.'
  },
  {
    timeline: [
      {
        moved: MovedState.Down,
        ringId: 'hold',
        date: new Date('2026-07-15'),
        description: 'Not selected as official coding agent.'
      },
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'OpenAI cloud-based coding agent.'
      }
    ],
    url: 'https://sennder.atlassian.net/wiki/spaces/AE/pages/5149065234/Decision+AI+Coding+Tooling+Copilot+Claude+Code+July+2026',
    key: 'codex',
    id: 'codex',
    title: 'Codex',
    quadrant: '',
    description:
      'OpenAI cloud-based coding agent that runs in a sandboxed environment. Can execute multi-step coding tasks asynchronously, including writing code, running tests, and creating pull requests. Evaluated alongside Claude Code but not selected as the official sennder coding assistant.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'hold',
        date: new Date('2026-05-27'),
        description: 'Not approved by IT/legal.'
      }
    ],
    url: 'https://cursor.sh/',
    key: 'cursor',
    id: 'cursor',
    title: 'Cursor',
    quadrant: '',
    description:
      'AI-native code editor with strong multi-file editing capabilities. NOT approved for sennder use due to licensing costs, missing data processing agreements, and lack of IT/legal approval. Use Claude Code instead.'
  }
]

// ========== LANGUAGES & FRAMEWORKS ==========

const languagesFrameworksEntries: Array<RadarEntry> = [
  // Adopt
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'adopt',
        date: new Date('2026-05-27'),
        description: 'Standard framework for building MCP servers.'
      }
    ],
    url: 'https://gofastmcp.com/getting-started/welcome',
    key: 'fastmcp',
    id: 'fastmcp',
    title: 'FastMCP',
    quadrant: '',
    description:
      'A high-level framework for building MCP servers quickly and idiomatically in Python. Provides decorators and utilities to expose tools, resources, and prompts to AI assistants with minimal boilerplate. The recommended way to create custom MCP integrations at sennder.'
  },
  // Trial
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'AWS-backed agent framework.'
      }
    ],
    url: 'https://strandsagents.com/',
    key: 'strands-agents',
    id: 'strands-agents',
    title: 'Strands Agents',
    quadrant: '',
    description:
      'An open-source SDK for building AI agents that can reason, plan, and take actions using tools. Supports multi-agent orchestration, memory, and tool use patterns. Being trialed for building internal autonomous engineering agents.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Type-safe AI agent framework.'
      }
    ],
    url: 'https://pydantic.dev/docs/ai/overview/',
    key: 'pydantic-ai',
    id: 'pydantic-ai',
    title: 'Pydantic AI',
    quadrant: '',
    description:
      'A Python framework for building AI agents with type safety and structured outputs built on Pydantic. Provides dependency injection, tool definitions, and multi-model support. Useful for building reliable, production-grade AI features with validated inputs and outputs.'
  },
  {
    timeline: [
      {
        moved: MovedState.NoChange,
        ringId: 'trial',
        date: new Date('2026-05-27'),
        description: 'Multi-agent orchestration framework.'
      }
    ],
    url: 'https://github.com/crewaiinc/crewai',
    key: 'crewai',
    id: 'crewai',
    title: 'CrewAI',
    quadrant: '',
    description:
      'A framework for orchestrating role-playing AI agents that collaborate on complex tasks. Define agents with specific roles, goals, and tools, then compose them into crews that work together. Being explored for multi-step engineering automation workflows.'
  }
  // Hold
]

export const AI_QUADRANTS = {
  SENNDER_TOOLS: {
    id: 'sennder_tools',
    name: 'Sennder-built AI Tools and Integrations',
    entries: sennderEntries
  },
  TOOLS: {
    id: 'tools',
    name: 'AI Artifacts, MCPs and Skills',
    entries: toolsEntries
  },
  PLATFORMS: {
    id: 'platforms',
    name: 'AI Tools and Platforms',
    entries: platformsEntries
  },
  LANGUAGES_FRAMEWORKS: {
    id: 'languages-frameworks',
    name: 'AI Infrastructure and Frameworks',
    entries: languagesFrameworksEntries
  }
}
